import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { MessageSquare, Plus, X, Send, ArrowLeft } from "lucide-react";
import { EarlyReleaseBanner, EarlyReleaseDialog, ReleaseStatusPill } from "@/components/EarlyReleaseRequest";

const PRIORITY_LABEL = { low: "Low", normal: "Normal", high: "High", urgent: "Urgent" };
const PRIORITY_COLOUR = {
  low: "bg-sand-light text-moss",
  normal: "bg-forest text-white",
  high: "bg-gold text-white",
  urgent: "bg-clay text-white",
};

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [active, setActive] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showRelease, setShowRelease] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ subject: "", body: "", priority: "normal" });
  const [reply, setReply] = useState("");

  const load = async () => {
    try { const r = await api.get("/support/tickets"); setTickets(r.data || []); }
    catch (e) { setError(formatErr(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (form.subject.length < 3 || form.body.length < 3) {
      setError("Subject and message must be at least 3 characters.");
      return;
    }
    setBusy(true); setError("");
    try {
      const r = await api.post("/support/tickets", form);
      setForm({ subject: "", body: "", priority: "normal" });
      setShowCreate(false);
      setActive(r.data);
      await load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const r = await api.post(`/support/tickets/${active.id}/reply`, { body: reply.trim() });
      setActive(r.data);
      setReply("");
      await load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        {!active && (
          <>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Support</div>
                <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">How can we help?</h1>
                <p className="text-moss mt-3 max-w-2xl">Real people read every ticket. Early-release requests get an answer within 24 hours.</p>
              </div>
              <button onClick={() => setShowCreate(true)}
                data-testid="new-ticket-btn"
                className="inline-flex items-center gap-2 bg-forest text-white rounded-xl px-5 py-3 text-sm font-medium hover:bg-forest-deep transition-colors">
                <Plus className="w-4 h-4" /> New ticket
              </button>
            </div>

            <EarlyReleaseBanner onOpen={() => setShowRelease(true)} pending={tickets.some(t => t.kind === "early_release" && t.release?.status === "requested")} />

            {error && <div className="mt-6 text-sm text-clay bg-clay-light px-4 py-3 rounded-lg" data-testid="support-error">{error}</div>}

            {tickets.length === 0 && (
              <div className="mt-10 bg-linen border border-sand rounded-2xl p-12 text-center" data-testid="support-empty">
                <MessageSquare className="w-10 h-10 text-forest mx-auto" strokeWidth={1.5} />
                <h3 className="font-serif text-2xl mt-4">No tickets yet</h3>
                <p className="text-sm text-moss mt-2">Open one and we&apos;ll get back to you.</p>
              </div>
            )}

            <div className="mt-8 space-y-3" data-testid="support-tickets-list">
              {tickets.map(t => (
                <button key={t.id} onClick={() => setActive(t)}
                  data-testid={`ticket-row-${t.id}`}
                  className="w-full bg-linen border border-sand rounded-xl p-5 text-left hover:border-forest transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <h3 className="font-serif text-lg text-ink truncate">{t.subject}</h3>
                      <p className="text-sm text-moss truncate mt-1">{t.body}</p>
                      <div className="flex items-center gap-2 mt-2 text-[11px] text-sage">
                        <span>{new Date(t.created_at).toLocaleString()}</span>
                        <span>·</span>
                        <span>{t.messages?.length || 0} {t.messages?.length === 1 ? "reply" : "replies"}</span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <ReleaseStatusPill ticket={t} />
                      <span className={`text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded ${PRIORITY_COLOUR[t.priority]}`}>
                        {PRIORITY_LABEL[t.priority]}
                      </span>
                      <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded bg-sand-light text-moss">
                        {t.status}
                      </span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

        {active && (
          <div data-testid="ticket-detail">
            <button onClick={() => setActive(null)}
              className="inline-flex items-center gap-1.5 text-sm text-moss hover:text-ink mb-6"
              data-testid="ticket-back-btn">
              <ArrowLeft className="w-4 h-4" /> All tickets
            </button>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Ticket</div>
                <h1 className="font-serif text-3xl tracking-tight mt-2">{active.subject}</h1>
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                <ReleaseStatusPill ticket={active} />
                <span className={`text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded ${PRIORITY_COLOUR[active.priority]}`}>
                  {PRIORITY_LABEL[active.priority]}
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 rounded bg-sand-light text-moss" data-testid="ticket-status">
                  {active.status}
                </span>
              </div>
            </div>

            {active.kind === "early_release" && active.release && (
              <div className="mt-6 rounded-xl border border-gold/50 bg-gold/10 p-4 text-sm" data-testid="ticket-release-summary">
                <strong>${Number(active.release.amount).toFixed(2)}</strong> from <strong>{active.release.vault_name}</strong>
                {active.release.status === "requested" && <span className="text-moss"> · an agent will reply within 24 hours</span>}
                {active.release.status === "approved" && <span className="text-forest"> · approved — it&apos;s in your Spending</span>}
                {active.release.status === "denied" && <span className="text-clay"> · not approved</span>}
              </div>
            )}
            <div className="mt-6 bg-linen border border-sand rounded-xl p-5 text-sm text-ink whitespace-pre-wrap" data-testid="ticket-body">
              {active.body}
            </div>

            <div className="mt-6 space-y-3" data-testid="ticket-messages">
              {(active.messages || []).map((m, i) => (
                <div key={i} className={`rounded-xl p-4 text-sm whitespace-pre-wrap ${
                  m.is_admin
                    ? "bg-forest text-cream ml-8"
                    : "bg-white border border-sand mr-8"
                }`} data-testid={`ticket-message-${i}`}>
                  <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold mb-2 ${m.is_admin ? "text-sage-mid" : "text-sage"}`}>
                    {m.is_admin ? "Support" : "You"} · {new Date(m.created_at).toLocaleString()}
                  </div>
                  {m.body}
                </div>
              ))}
            </div>

            {active.status !== "closed" && (
              <div className="mt-6 bg-linen border border-sand rounded-xl p-4">
                <textarea value={reply} onChange={e => setReply(e.target.value)}
                  placeholder="Type a reply…" rows={3} maxLength={10000}
                  data-testid="ticket-reply-input"
                  className="w-full bg-white border border-sand rounded-md px-3 py-2 text-sm resize-none" />
                <div className="flex justify-end mt-2">
                  <button onClick={sendReply} disabled={busy || !reply.trim()}
                    data-testid="ticket-reply-btn"
                    className="inline-flex items-center gap-1.5 bg-forest text-white rounded-md px-4 py-2 text-xs font-medium hover:bg-forest-deep disabled:opacity-60">
                    <Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send reply"}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {showRelease && <EarlyReleaseDialog onClose={() => setShowRelease(false)} onDone={(t) => { setShowRelease(false); setActive(t); load(); }} />}

        {showCreate && (
          <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-cream rounded-2xl border border-sand p-8 max-w-lg w-full" data-testid="new-ticket-modal">
              <div className="flex items-start justify-between">
                <h3 className="font-serif text-2xl">New ticket</h3>
                <button onClick={() => setShowCreate(false)} className="text-sage hover:text-ink"><X className="w-5 h-5" /></button>
              </div>
              <div className="mt-6 space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Subject</label>
                  <input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })}
                    placeholder="Briefly describe the issue" maxLength={200}
                    data-testid="ticket-subject-input"
                    className="w-full mt-1.5 bg-white border border-sand rounded-md px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Priority</label>
                  <div className="grid grid-cols-4 gap-1.5 mt-2">
                    {["low", "normal", "high", "urgent"].map(p => (
                      <button key={p} onClick={() => setForm({ ...form, priority: p })}
                        data-testid={`priority-${p}`}
                        className={`text-xs py-2 rounded-lg border ${
                          form.priority === p
                            ? "bg-forest text-white border-forest"
                            : "bg-white text-moss border-sand"
                        }`}>
                        {PRIORITY_LABEL[p]}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Message</label>
                  <textarea value={form.body} onChange={e => setForm({ ...form, body: e.target.value })}
                    placeholder="Share as much detail as you can…" rows={6} maxLength={10000}
                    data-testid="ticket-body-input"
                    className="w-full mt-1.5 bg-white border border-sand rounded-md px-3 py-2 text-sm resize-none" />
                </div>
              </div>
              <button onClick={create} disabled={busy}
                data-testid="ticket-submit-btn"
                className="w-full mt-6 bg-forest text-white rounded-xl py-3 text-sm font-medium hover:bg-forest-deep disabled:opacity-60">
                {busy ? "Sending…" : "Submit ticket"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
